/**
 * Lieu d'un ÉVÉNEMENT ou d'une ADHÉSION tel que l'affichent les listes, les
 * titres SEO, les images de partage et le JSON-LD de donoor.org (serveur Nitro
 * ET pages) — lot 4 de l'internationalisation.
 *
 * Contrat : docs/architecture/adresses-structurees.md, §10 (« `formaterLieu`
 * partout, à la place des concaténations » ; « Sainte-Luce, Martinique »).
 *
 * POURQUOI CE FICHIER : les cartes de la recherche, les colonnes de la page
 * association, les contenus liés, les titres et les images de partage
 * affichaient `location_city` seul (un Saint-Denis de La Réunion ressemblait à
 * celui du 93) ou recollaient « nom, ville » à la main. Ce ne sont ici que des
 * ADAPTATEURS des champs `location_*` vers la règle partagée de
 * `@donoor/utils` (`libelleLieuCourt`, `formaterLieu`) : aucune règle locale.
 *
 * Tolérance : chaque champ est facultatif. Sans `location_country` ni
 * `location_territory` (API d'avant la migration 307, ancien lieu non
 * converti), le lieu est lu comme en métropole : la ville seule, comme avant.
 */
import { formaterLieu, libelleLieuCourt, replierBlancs, TERRITOIRES_OUTRE_MER } from "@donoor/utils";

/** Champs de lieu publics (API : `location_*`, `CHAMPS_LIEU_PUBLICS`), tous facultatifs. */
export interface LieuPublicLu {
  location_name?: string | null;
  location_street?: string | null;
  location_postcode?: string | null;
  location_city?: string | null;
  location_country?: string | null;
  location_territory?: string | null;
  location_latitude?: number | null;
  location_longitude?: number | null;
}

/** Pays et territoire du lieu, au format de la règle partagée. */
function structureLieu(lieu: LieuPublicLu): { pays: string | null; territoire: string | null } {
  return { pays: lieu.location_country ?? null, territoire: lieu.location_territory ?? null };
}

/**
 * Ville courte du lieu : « Saint-Denis, La Réunion » · « Paris » ·
 * « Bruxelles, Belgique ». Sans ville : territoire ou pays hors métropole,
 * sinon chaîne vide (rien à afficher).
 */
export function villeCourteLieu(lieu: LieuPublicLu | null | undefined): string {
  if (!lieu) return "";
  return libelleLieuCourt({ ville: lieu.location_city, ...structureLieu(lieu) }) ?? "";
}

/**
 * Lieu en UNE ligne pour une carte de liste : nom, ville, puis territoire ou
 * pays hors métropole, sans doublon (« Salle des fêtes, Saint-Denis, La
 * Réunion »). Chaîne vide quand rien n'est connu.
 */
export function ligneLieuCarte(lieu: LieuPublicLu | null | undefined): string {
  if (!lieu) return "";
  return formaterLieu({ nom: lieu.location_name, ville: lieu.location_city, structure: structureLieu(lieu) });
}

/**
 * `PostalAddress` JSON-LD (§10) depuis des MORCEAUX : rue, code postal,
 * ville, territoire (`addressRegion`, son nom) et pays réel. `null` quand ni
 * rue, ni code postal, ni ville ne sont connus (un pays seul n'est pas une
 * adresse). Pays absent : France (lieu d'avant la migration 307).
 */
export function adressePostaleSchema(m: {
  rue?: string | null;
  codePostal?: string | null;
  ville?: string | null;
  territoire?: string | null;
  pays?: string | null;
}): Record<string, unknown> | null {
  const rue = replierBlancs(m.rue);
  const codePostal = replierBlancs(m.codePostal);
  const ville = replierBlancs(m.ville);
  if (!rue && !codePostal && !ville) return null;
  const nomTerritoire = TERRITOIRES_OUTRE_MER.find((t) => t.code === m.territoire)?.nom ?? null;
  return {
    "@type": "PostalAddress",
    ...(rue ? { streetAddress: rue } : {}),
    ...(codePostal ? { postalCode: codePostal } : {}),
    ...(ville ? { addressLocality: ville } : {}),
    ...(nomTerritoire ? { addressRegion: nomTerritoire } : {}),
    addressCountry: replierBlancs(m.pays).toUpperCase() || "FR",
  };
}

/** `GeoCoordinates` (propriété valide d'un `Place`) quand le point du lieu est connu. */
export function geoLieuSchema(lieu: LieuPublicLu): Record<string, unknown> | null {
  const { location_latitude: latitude, location_longitude: longitude } = lieu;
  if (typeof latitude !== "number" || typeof longitude !== "number") return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { "@type": "GeoCoordinates", latitude, longitude };
}

/**
 * `Place` JSON-LD d'un lieu lu dans une LISTE (hubs de l'annuaire) : nom,
 * adresse depuis les morceaux exposés et point. Les listes publiques
 * n'exposent pas le texte de l'adresse : pas d'analyse ici, les morceaux
 * suffisent (ville, code postal, territoire, pays). `null` quand rien n'est
 * connu du lieu.
 */
export function lieuSchemaDepuisMorceaux(lieu: LieuPublicLu | null | undefined): Record<string, unknown> | null {
  if (!lieu) return null;
  const address = adressePostaleSchema({
    rue: lieu.location_street,
    codePostal: lieu.location_postcode,
    ville: lieu.location_city,
    ...structureLieu(lieu),
  });
  const geo = geoLieuSchema(lieu);
  const nom = replierBlancs(lieu.location_name) || villeCourteLieu(lieu);
  if (!nom && !address && !geo) return null;
  return {
    "@type": "Place",
    ...(nom ? { name: nom } : {}),
    ...(address ? { address } : {}),
    ...(geo ? { geo } : {}),
  };
}
